import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_STATUS_NAMES, DEFAULT_TICKET_STATUS_CONFIG, type TicketStatusConfigResponse } from '@atlas/contracts';
import { api } from './api';
import { STATUS_LABEL } from './format';

export const TICKET_STATUS_QUERY_KEY = ['ticket-status-config'] as const;
function useStatusQuery() {
  return useQuery({ queryKey: TICKET_STATUS_QUERY_KEY,
    queryFn: () => api.get<TicketStatusConfigResponse>('/v2/ticket-status-config'),
    staleTime: 30_000, refetchInterval: 60_000,
  });
}
const StatusContext = createContext<ReturnType<typeof useStatusQuery> | null>(null);
export function TicketStatusProvider({ children }: { children: ReactNode }) {
  const query = useStatusQuery();
  return createElement(StatusContext.Provider, { value: query }, children);
}
export function useTicketStatusConfig() {
  const query = useContext(StatusContext);
  if (!query) throw new Error('TicketStatusProvider is missing');
  return query;
}
export function useStatusLabels(): Record<string, string> {
  const query = useTicketStatusConfig();
  const config = query.data?.config ?? DEFAULT_TICKET_STATUS_CONFIG;
  return useMemo(() => ({ ...STATUS_LABEL, ...Object.fromEntries(config.statuses.map(s => {
    if (s.label !== DEFAULT_STATUS_NAMES[s.id] || !/^[PE] · /.test(STATUS_LABEL[s.id] ?? '')) return [s.id, s.label];
    const product = s.departments.product !== 'hidden', engineering = s.departments.engineering !== 'hidden';
    const prefix = product && engineering ? 'P / E' : product ? 'P' : engineering ? 'E'
      : s.departments.support !== 'hidden' ? 'S' : s.departments.devops !== 'hidden' ? 'D' : '';
    return [s.id, prefix ? `${prefix} · ${s.label}` : s.label];
  })) }), [config]);
}
